import React, { memo, useState, useEffect, useCallback } from 'react';
import { registerLicense } from '@syncfusion/ej2-base';
import axios from 'axios';
import PropTypes from 'prop-types';
import { BACKEND_URL } from '../utils/constants';
import ZingChart from './ZingChart';
import ZingDashboard from './ZingDashboard';

const Zing = ({ apiKey, embedUrl, className, token, zingKey }) => {
  registerLicense(zingKey);

  const [loadingValidation, setLoadingValidation] = useState(false);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [error, setError] = useState();
  const [jobId, setJobId] = useState();
  const [chartType, setChartType] = useState('BarChart');
  const [questionData, setQuestionData] = useState();
  const [dashboardData, setDashboardData] = useState();
  const [embedId, setEmbedId] = useState();
  const [whereProperty, setWhereProperty] = useState();
  const [embedType, setEmbedType] = useState();

  const validateUrl = useCallback(async () => {
    setLoadingValidation(true);
    const url = embedUrl;
    const urlElements = url.split('&');
    const signatureString = urlElements.pop();
    const signature = signatureString.split('=').pop();

    const body = {
      url: urlElements.join('&'),
      signature: signature,
    };

    const result = await axios.post(`${BACKEND_URL}/api/v1/embeds/validate`, body, {
      headers: {
        'Content-Type': 'application/json',
        'x-zingdata-api': apiKey,
      },
    });

    if (result?.data?.embedId) {
      setEmbedId(result.data.embedId);
      setWhereProperty(result.data.where);
      setEmbedType(result.data.type);
    }

    setLoadingValidation(false);
  }, [apiKey, embedUrl]);

  const fetchQuestion = useCallback(async () => {
    setLoadingQuestion(true);
    try {
      const body = whereProperty ? JSON.parse(whereProperty) : {};
      const result = await axios.post(`${BACKEND_URL}/api/v1/embeds/${embedId}/chart?token=${token}`, body, {
        headers: {
          'Content-Type': 'application/json',
          'x-zingdata-api': apiKey,
        },
      });

      if (!result) {
        setError('Could not fetch result');
      }

      if (result.data?.chartType) {
        setChartType(result.data?.chartType);
      }
      setQuestionData({ questionId: result.data?.questionId, questionText: result.data?.questionText });

      if (result.data?.chartType === 'DataTable') {
        setJobId(result.data?.tableJobId?.id);
      } else {
        setJobId(result.data?.chartJobId?.id);
      }
      setLoadingQuestion(false);
      return result;
    } catch (e) {
      setError(e);
      setLoadingQuestion(false);
    }
  }, [apiKey, embedId, whereProperty, token]);

  const fetchDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const body = whereProperty ? JSON.parse(whereProperty) : {};
      const result = await axios.post(`${BACKEND_URL}/api/v1/embeds/${embedId}/dashboard?token=${token}`, body, {
        headers: {
          'Content-Type': 'application/json',
          'x-zingdata-api': apiKey,
        },
      });

      if (!result) {
        setError('Could not fetch result');
      }

      setDashboardData({
        dashboardId: result.data?.dashboardId,
        dashboardTitle: result.data?.dashboardTitle,
        questions: result.data?.results,
      });

      setLoadingDashboard(false);
      return result;
    } catch (e) {
      setError(e);
      setLoadingDashboard(false);
    }
  }, [apiKey, embedId, whereProperty, token]);

  useEffect(() => {
    if (embedUrl) {
      validateUrl();
    }
  }, [embedUrl, validateUrl]);

  useEffect(() => {
    if (embedId) {
      if (embedType === 'chart') {
        fetchQuestion();
      } else {
        fetchDashboard();
      }
    }
  }, [embedId, fetchQuestion, fetchDashboard, embedType]);

  if (embedType === 'chart') {
    return (
      <div className={`zing-data-body ${className || ''}`}>
        <ZingChart
          apiKey={apiKey}
          token={token}
          loadingValidation={loadingValidation}
          loadingQuestion={loadingQuestion}
          questionData={questionData}
          chartType={chartType}
          jobId={jobId}
        />
      </div>
    );
  }

  return (
    <div className={`zing-data-body ${className || ''}`}>
      <ZingDashboard
        apiKey={apiKey}
        token={token}
        loadingValidation={loadingValidation}
        loadingDashboard={loadingDashboard}
        dashboardData={dashboardData}
      />
    </div>
  );
};

Zing.propTypes = {
  apiKey: PropTypes.string.isRequired,
  embedUrl: PropTypes.string.isRequired,
  zingKey: PropTypes.string.isRequired,
  token: PropTypes.string,
  where: PropTypes.string,
  className: PropTypes.string,
};

export default memo(Zing);
